import { installGlobalReporting } from './core/report';
import './styles/fonts.css';
import './generated/tokens.css';
import './styles/base.css';
import './styles/app.css';

import { Machine } from './core/machine';
import { canInteract } from './core/stages';
import { advance } from './stages/driver';
import { mountScene } from './stages/scene';
import { BroadcastEngine } from './audio/engine';
import { mountStatusDisplay } from './ui/statusDisplay';
import { mountDropTarget, isAudio } from './ui/dropTarget';
import { mountHeroKnob } from './ui/heroKnob';
import { mountTransportControls } from './ui/transportControls';
import { mountPointHotspot } from './ui/hotspot';
import { CODI_COPY } from './core/constants';

// Centre of the R27's CRT screen, measured as % of the studio plate (ENG-069
// pattern) from CC_TRANSITION_001..._endframe.webp (1920x1080).
const CRT_CENTER: readonly [number, number] = [50.26, 31.76];

const root = document.querySelector<HTMLDivElement>('#receiver-root');
if (!root) throw new Error('receiver-root missing from index.html');

// A file dropped outside the receiver must not navigate the browser away.
// This guard only cancels the browser default; file acceptance remains
// exclusively scoped to mountDropTarget's .cc-station listeners.
const preventFileDropNavigation = (event: DragEvent): void => {
  const isFileDrag = Array.from(event.dataTransfer?.types ?? []).includes('Files');
  if (isFileDrag) event.preventDefault();
};

document.addEventListener('dragover', preventFileDropNavigation, true);
document.addEventListener('drop', preventFileDropNavigation, true);

const machine = new Machine();
const engine = new BroadcastEngine();

// Aspect-locked stage: background plate, transition video, and the receiver
// overlay all share this one box so they scale and align together (Milestone 2 —
// studio arrival). Future ambient layers (glow, city lights, Codi) mount here too.
const stage = document.createElement('div');
stage.className = 'cc-stage';

const station = document.createElement('div');
station.className = 'cc-station';

const crtReadout = document.createElement('div');
crtReadout.className = 'cc-crt-readout';
stage.append(crtReadout);
mountPointHotspot(crtReadout, CRT_CENTER);

// V2 — Codeine Castle's entry point: a hidden hit-target on the CRT glass, not
// wrapping crtReadout. NO SIGNAL is a visual broadcast on the screen; this is a
// separate transparent button underneath it, so neither affects the other.
// Appended to `stage` after `station` (see boot()) so it tabs last — a
// secret, not the first thing a keyboard/screen-reader visitor meets.
const castleHotspot = document.createElement('button');
castleHotspot.type = 'button';
castleHotspot.className = 'cc-castle-hotspot';
castleHotspot.setAttribute('aria-label', 'Enter Codeine Castle');
castleHotspot.addEventListener('click', () => { void enterCastle(); });

const renderStatus = mountStatusDisplay(crtReadout);
const renderKnob = mountHeroKnob(station, (v) => {
  machine.patch({ drift: v });
  engine.apply(v);
});
const renderTransport = mountTransportControls(station, {
  onPlay: () => { engine.resume(); machine.patch({ playing: true }); },
  onPause: () => { engine.pause(); machine.patch({ playing: false }); },
  onStop: () => { engine.stop(); machine.patch({ playing: false }); },
});
const renderDrop = mountDropTarget(station, (file) => { void acceptSignal(file); });

const comingSoon = document.createElement('p');
comingSoon.className = 'cc-coming-soon';
comingSoon.textContent = CODI_COPY.nativeComingSoon;

machine.subscribe((c) => {
  document.body.dataset['state'] = c.state;
  renderStatus(c);
  renderKnob(c, canInteract(c.state, 'knob'));
  renderTransport(c, canInteract(c.state, 'transport'));
  renderDrop(c, canInteract(c.state, 'drop'));
});

/** The visitor feeds a signal into the receiver. */
async function acceptSignal(file: File): Promise<void> {
  if (!isAudio(file)) {
    machine.fail('That signal cannot be read.');
    return;
  }
  machine.go('ACCEPTING', { fileName: file.name, failure: null });
  try {
    await engine.init();
    await engine.load(file);
  } catch {
    machine.fail(`Could not hold ${machine.ctx.broadcastName}.`);
    return;
  }
  machine.go('WAKING');
  await advance(stage, machine);          // WAKING → LOCKING → REALM
  engine.play(true);
  engine.apply(machine.ctx.drift);
  machine.patch({ playing: true });
}

// Guards the async mount() window only — once mounted, the game's own
// full-viewport overlay (z-index 50) physically covers castleHotspot
// (z-index 5), so it can't be clicked again until onExit() runs.
let enteringCastle = false;

/** The secret door: click the CRT, play the climb, exit back to the studio in place. */
async function enterCastle(): Promise<void> {
  if (enteringCastle) return;
  enteringCastle = true;
  try {
    if (engine.playing) { engine.pause(); machine.patch({ playing: false }); }
    await engine.init();
    const audioContext = engine.context;
    if (!audioContext) return; // init() always sets this; defensive only
    const { mount, unmount } = await import('./realms/codeine-castle');
    const container = document.createElement('div');
    document.body.append(container);
    await mount({
      container,
      audioContext,
      onExit: () => { unmount(); container.remove(); },
    });
  } finally {
    enteringCastle = false;
  }
}

async function boot(): Promise<void> {
  /* First thing, before anything can fail. See core/report.ts. */
  installGlobalReporting();
  await mountScene(stage);
  stage.append(station);
  stage.append(castleHotspot);
  root!.append(stage, comingSoon);
  machine.go('STARTUP');
  await advance(stage, machine);           // STARTUP → ARRIVED → IDLE_PROMPT
}

void boot();
